/**
 * MailQuotaBanner (OCP-249) - proactive amber banner on the /mail
 * surfaces so a customer learns they can't send BEFORE they click "Send
 * test" and hit a hard 503. Two states, both derived from
 * GET /v1/portal/mail/overview:
 *
 *   no-subscription          -> no active oms-family subscription
 *   quota-exhausted-no-overage -> active sub, base quota used up, overage off
 *
 * The subscription facts come from OCP's own DB (not a live OMS call), so
 * the no-subscription variant surfaces even when the OMS bridge is down.
 * Self-fetches so both /mail/overview and /mail/sends just drop it in.
 *
 * Dismiss persists via localStorage keyed on the state (+ period for the
 * quota case): dismissing hides THAT state; a state change (new period,
 * new sub) re-shows it.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface OverviewSubscription {
  has_active_subscription: boolean;
  allow_overage: boolean;
}

interface OverviewQuota {
  used: number;
  total: number | null;
  period_end: string | null;
}

interface OverviewResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  quota: OverviewQuota | null;
  subscription: OverviewSubscription | null;
}

type BannerState = 'no-subscription' | 'quota-exhausted-no-overage' | null;

const DISMISS_KEY = 'orboto.mailQuotaBanner.dismissed';

function deriveState(d: OverviewResponse): { state: BannerState; periodEnd: string | null } {
  if (!d.oms_configured || !d.subscription) return { state: null, periodEnd: null };
  if (!d.subscription.has_active_subscription) {
    return { state: 'no-subscription', periodEnd: null };
  }
  const q = d.quota;
  if (q && q.total !== null && q.used >= q.total && !d.subscription.allow_overage) {
    return { state: 'quota-exhausted-no-overage', periodEnd: q.period_end };
  }
  return { state: null, periodEnd: null };
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export function MailQuotaBanner() {
  const { t } = useTranslation('mail-quota');
  const [info, setInfo] = useState<{ state: BannerState; periodEnd: string | null } | null>(null);
  const [dismissedKey, setDismissedKey] = useState<string | null>(readDismissed);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const d = await api<OverviewResponse>('/v1/portal/mail/overview', {
          signal: ac.signal,
        });
        setInfo(deriveState(d));
      } catch {
        // OMS down / transient - say nothing rather than a false banner.
        setInfo({ state: null, periodEnd: null });
      }
    })();
    return () => ac.abort();
  }, []);

  if (!info || info.state === null) return null;
  const stateKey = `${info.state}:${info.periodEnd ?? ''}`;
  if (dismissedKey === stateKey) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, stateKey);
    } catch {
      // localStorage unavailable (private mode) - dismiss for this mount only.
    }
    setDismissedKey(stateKey);
  };

  const content =
    info.state === 'no-subscription'
      ? {
          headline: t('quota.noSubscription.headline'),
          detail: t('quota.noSubscription.detail'),
          cta: t('quota.noSubscription.cta'),
          to: '/products',
        }
      : {
          headline: t('quota.exhausted.headline', {
            period: info.periodEnd ? t('quota.exhausted.period', { date: formatDate(info.periodEnd) }) : '',
          }),
          detail: t('quota.exhausted.detail'),
          cta: t('quota.exhausted.cta'),
          to: '/mail/settings',
        };

  return (
    <div
      role="alert"
      data-testid="mail-quota-banner"
      data-state={info.state}
      className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
    >
      <div>
        <p className="font-medium">{content.headline}</p>
        <p className="mt-0.5 text-amber-800 dark:text-amber-300/90">
          {content.detail}{' '}
          <Link to={content.to} className="font-medium underline hover:no-underline">
            {content.cta}
          </Link>
          .
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('quota.dismiss')}
        className="shrink-0 rounded p-1 leading-none text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
      >
        ✕
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}
